import { RefreshCw } from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import { RunComparisonView } from "@/components/walkthrough/run-comparison-view";
import { WorkspaceTabsStrip } from "./workspace-tabs-strip";
import { WorkspaceFindingsTab } from "./workspace-findings-tab";
import { WorkspaceTraceTab } from "./workspace-trace-tab";
import { WorkspaceSidebar } from "./workspace-sidebar";
import { WorkspaceFailureDiagnostics } from "./workspace-failure-diagnostics";
import type { WalkthroughFinding, FindingKind } from "./workspace-types";
import type { WorkspaceTab } from "./use-workspace-search-params";

export interface WorkspaceWorkbenchProps {
  walkthrough: any;
  walkthroughSteps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
    pageUrl?: string | null;
    surface?: string | null;
  }>;
  allFindings: WalkthroughFinding[];
  filteredFindings: WalkthroughFinding[];
  problemsCount: number;
  helpsCount: number;
  openCount: number;
  verdicts: any;
  findingTitles: Map<string, string>;
  kindFilter: FindingKind;
  onKindFilterChange: (kind: FindingKind) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (severity: string) => void;
  selectedIds: Set<string>;
  focusedIndex: number;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (id: string, checked: boolean) => void;
  onOpenFinding: (id: string) => void;
  onOpenZenMode: (index?: number) => void;
  activeTab: WorkspaceTab;
  setActiveTab: (tab: WorkspaceTab) => void;
  stepNumberFromUrl: number | null;
  scenarioRuns: any[] | undefined;
  runsChronological: any[];
  runNumber: number | undefined;
  defaultBaseRunId: string | null;
  activeBaseRunId: string | null;
  selectBaseRun: (id: string) => void;
  projectId: string;
  canEdit: boolean;
  hasLinkedRepo: boolean;
  isTrackerConfigured: boolean;
  guidanceRules: any[];
  htmlReport: any;
  setReportOpen: (open: boolean) => void;
  mutations: any;
  isRunning: boolean;
  isFailed: boolean;
}

export function WorkspaceWorkbench({
  walkthrough,
  walkthroughSteps,
  allFindings,
  filteredFindings,
  problemsCount,
  helpsCount,
  openCount,
  verdicts,
  findingTitles,
  kindFilter,
  onKindFilterChange,
  statusFilter,
  onStatusFilterChange,
  severityFilter,
  onSeverityFilterChange,
  selectedIds,
  focusedIndex,
  allFilteredSelected,
  someFilteredSelected,
  onToggleSelectAll,
  onToggleSelect,
  onOpenFinding,
  onOpenZenMode,
  activeTab,
  setActiveTab,
  stepNumberFromUrl,
  scenarioRuns,
  runsChronological,
  runNumber,
  defaultBaseRunId,
  activeBaseRunId,
  selectBaseRun,
  projectId,
  canEdit,
  hasLinkedRepo,
  isTrackerConfigured,
  guidanceRules,
  htmlReport,
  setReportOpen,
  mutations,
  isRunning,
  isFailed,
}: WorkspaceWorkbenchProps) {
  const persona = walkthrough.persona;
  const primaryVerdict = verdicts && verdicts.length > 0 ? verdicts[0] : null;
  const hasHeroVerdict =
    walkthrough.status === "completed" && Boolean(primaryVerdict?.reasoning);
  const tabVerdicts = hasHeroVerdict ? verdicts?.slice(1) : verdicts;

  return (
    <div className="grid gap-8 pb-28 lg:grid-cols-[minmax(0,1fr)_22rem] print:hidden">
      <div className="space-y-6 min-w-0">
        <WorkspaceTabsStrip
          activeTab={activeTab}
          onTabChange={setActiveTab}
          findingsCount={allFindings.length}
          stepsCount={walkthroughSteps.length}
        />

        {isRunning && (
          <Tonal padding="lg" className="space-y-2 border-border/80">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <Kicker>Execution in progress</Kicker>
            </div>
            <p className="text-sm text-foreground">
              {persona
                ? `${persona.name} is walking through this situation now.`
                : "Walkthrough in progress."}{" "}
              Steps and observations will stream here automatically.
            </p>
          </Tonal>
        )}

        {isFailed && (
          <WorkspaceFailureDiagnostics
            walkthrough={walkthrough}
            stepsCount={walkthroughSteps.length}
          />
        )}

        {activeTab === "findings" && (
          <WorkspaceFindingsTab
            verdicts={tabVerdicts}
            findingTitles={findingTitles}
            isVerification={walkthrough.isVerification === true}
            walkthrough={walkthrough}
            walkthroughSteps={walkthroughSteps.map((s) => ({
              id: s.id,
              stepNumber: s.stepNumber,
            }))}
            allFindings={allFindings}
            filteredFindings={filteredFindings}
            problemsCount={problemsCount}
            helpsCount={helpsCount}
            openCount={openCount}
            kindFilter={kindFilter}
            onKindFilterChange={onKindFilterChange}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
            severityFilter={severityFilter}
            onSeverityFilterChange={onSeverityFilterChange}
            personaName={persona?.name}
            projectId={projectId}
            canEdit={canEdit}
            hasLinkedRepo={hasLinkedRepo}
            selectedIds={selectedIds}
            focusedIndex={focusedIndex}
            allFilteredSelected={allFilteredSelected}
            someFilteredSelected={someFilteredSelected}
            onToggleSelectAll={onToggleSelectAll}
            onToggleSelect={onToggleSelect}
            onOpenFinding={onOpenFinding}
            onSelectDeltaFinding={onOpenFinding}
            onOpenZenMode={onOpenZenMode}
          />
        )}

        {activeTab === "trace" && (
          <WorkspaceTraceTab
            walkthroughSteps={walkthroughSteps}
            findings={allFindings}
            walkthroughId={walkthrough.id}
            personaName={persona?.name}
            projectId={projectId}
            isFailed={isFailed}
            isRunning={isRunning}
            highlightedStepNumber={stepNumberFromUrl}
            onOpenFinding={onOpenFinding}
            onOpenZenMode={onOpenZenMode}
          />
        )}

        {activeTab === "compare" && (
          <RunComparisonView
            currentWalkthrough={walkthrough}
            currentRunNumber={runNumber}
            scenarioRuns={scenarioRuns ?? []}
            runsChronological={runsChronological}
            activeBaseRunId={activeBaseRunId}
            onSelectBaseRun={selectBaseRun}
            onOpenFinding={onOpenFinding}
            onCreateIssue={(canonId) =>
              mutations.setCreateIssueCanonicalId(canonId)
            }
            onLinkIssue={(canonId) =>
              mutations.setLinkExistingCanonicalId(canonId)
            }
            onBulkCreateIssues={(canonIds) => {
              mutations.setBulkComparisonCanonicalIds(canonIds);
              mutations.setBulkIssuesOpen(true);
            }}
            canEdit={canEdit}
            projectId={projectId}
            isTrackerConfigured={isTrackerConfigured}
          />
        )}
      </div>

      <WorkspaceSidebar
        walkthrough={walkthrough}
        scenarioRuns={scenarioRuns}
        runsChronological={runsChronological}
        runNumber={runNumber}
        walkthroughStepsCount={walkthroughSteps.length}
        problemsCount={problemsCount}
        defaultBaseRunId={defaultBaseRunId}
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        projectId={projectId}
        guidanceRules={guidanceRules}
        onOpenReport={
          htmlReport?.content ? () => setReportOpen(true) : undefined
        }
      />
    </div>
  );
}
